import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  Plus, Minus, Trash2, ShoppingCart, Lock, Search, X, AlertCircle, Tag,
} from "lucide-react";

import SEO from "../components/SEO";
import Schema from "../components/Schema";
import Breadcrumbs from "../components/Breadcrumbs";
import CtaBand from "../components/CtaBand";
import WebshopPageBanner from "../components/WebshopPageBanner";
import ProductLightbox from "../components/webshop/ProductLightbox";
import CheckoutWizard from "../components/webshop/CheckoutWizard";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../components/ui/dialog";

import { useCart } from "../context/CartContext";
import { useShopAuth } from "../context/ShopAuthContext";
import {
  fetchWebshopCatalog, fetchWebshopCategories, createWebshopCheckoutSession,
} from "../lib/api";

// ---------- helpers ----------
const fmtEur = (val) => `€ ${Number(val || 0).toFixed(2)}`;
const STOCK_LABELS = {
  in_stock: "Op voorraad",
  out_of_stock: "Niet op voorraad",
  backorder: "Backorder",
  "n/a": "Op aanvraag",
};
const STOCK_COLORS = {
  in_stock: "text-green-700",
  out_of_stock: "text-red-600",
  backorder: "text-amber-700",
  "n/a": "text-gray-500",
};

function effectivePrice(p) {
  if (p?.sale_price && Number(p.sale_price) > 0) return Number(p.sale_price);
  if (p?.regular_price && Number(p.regular_price) > 0) return Number(p.regular_price);
  return null;
}

// ==================================================================
// PRODUCT CARD
// ==================================================================
function ProductCard({ p, onAdd }) {
  const isVariable = p.product_type === "variable";
  const isService = p.product_type === "service";
  const variants = p.variations || [];
  const [variationId, setVariationId] = useState(
    isVariable && variants.length ? variants[0].id : ""
  );
  const [qty, setQty] = useState(1);

  const selectedVariant = isVariable
    ? variants.find((v) => v.id === variationId) || null
    : null;
  const sellable = selectedVariant || p;
  const price = effectivePrice(sellable);
  const buyable = isVariable ? Boolean(sellable?.stripe_price_id) : Boolean(p.stripe_price_id);
  const outOfStock =
    sellable?.stock_status === "out_of_stock" ||
    (sellable?.stock_status === "in_stock" && Number(sellable?.stock_qty || 0) <= 0);

  const stockKey = sellable?.stock_status || "n/a";

  // Phase 19-C — gallery thumbnail state (per-product selection)
  // Normalise gallery: accept both legacy List[str] and new List[obj]
  const galleryRaw = Array.isArray(p.gallery_images) ? p.gallery_images : [];
  const galleryItems = galleryRaw.map((g, i) =>
    typeof g === "string"
      ? { url: g, alt: `${p.name} foto ${i + 1}`, title: "", sort_order: i }
      : { url: g?.url || "", alt: g?.alt || `${p.name} foto ${i + 1}`, title: g?.title || "", sort_order: g?.sort_order ?? i }
  ).filter((g) => g.url);
  const [galleryIdx, setGalleryIdx] = useState(null); // null = main, 0..n = thumb idx

  // Phase 19-D — lightbox state + image-set composition
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxInitial, setLightboxInitial] = useState(0);

  // Phase 19-B/C image priority:
  //   1. selected variant image (when user picked variant with own image)
  //   2. selected gallery thumbnail (when user clicked a thumb)
  //   3. product main image
  //   4. fallback tile
  const variantImage = selectedVariant && selectedVariant.image_url;
  const galleryImage = galleryIdx !== null && galleryItems[galleryIdx];
  const displayImageUrl = variantImage
    || (galleryImage ? galleryImage.url : null)
    || p.image_url
    || "";
  const displayImageAlt = variantImage
    ? selectedVariant.name
    : galleryImage
      ? galleryImage.alt
      : (p.image_alt || p.name);
  const displayImageTitle = galleryImage?.title || p.image_title || p.name;

  // Phase 19-D — assemble full image list for lightbox.
  // Priority chain remains; variant image (if any) becomes index 0 so the
  // lightbox opens on what the user already sees on the card.
  const lightboxImages = useMemo(() => {
    const out = [];
    const seen = new Set();
    const push = (url, alt, title) => {
      if (!url || seen.has(url)) return;
      seen.add(url);
      out.push({ url, alt: alt || p.name, title: title || "" });
    };
    if (variantImage) push(variantImage, selectedVariant.name, "");
    if (p.image_url) push(p.image_url, p.image_alt || p.name, p.image_title);
    galleryItems.forEach((g) => push(g.url, g.alt, g.title));
    return out;
  }, [variantImage, selectedVariant, p, galleryItems]);

  // Compute the lightbox index that matches the currently displayed image
  const openLightbox = (urlSeed) => {
    const target = urlSeed || displayImageUrl;
    const idx = lightboxImages.findIndex((i) => i.url === target);
    setLightboxInitial(idx >= 0 ? idx : 0);
    setLightboxOpen(true);
  };

  // Preload variant image on variant switch to avoid flicker
  useEffect(() => {
    if (variantImage) {
      const img = new Image();
      img.src = variantImage;
    }
  }, [variantImage]);

  // Reset gallery state when variant changes (variant image takes priority)
  useEffect(() => {
    if (variantImage) setGalleryIdx(null);
  }, [variantImage]);

  return (
    <motion.div
      initial={{ opacity: 0, y: 14 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      className="card-premium flex flex-col overflow-hidden group"
      data-testid={`product-card-${p.slug}`}
    >
      <div
        className="aspect-[4/3] overflow-hidden bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center cursor-zoom-in group/img"
        onClick={() => displayImageUrl && openLightbox(displayImageUrl)}
        role={displayImageUrl ? "button" : undefined}
        tabIndex={displayImageUrl ? 0 : -1}
        onKeyDown={(e) => {
          if (displayImageUrl && (e.key === "Enter" || e.key === " ")) {
            e.preventDefault();
            openLightbox(displayImageUrl);
          }
        }}
        aria-label={displayImageUrl ? `Bekijk afbeeldingen voor ${p.name}` : undefined}
        data-testid={`product-image-clickable-${p.slug}`}
      >
        {displayImageUrl ? (
          <img
            src={displayImageUrl}
            alt={displayImageAlt}
            title={displayImageTitle}
            loading="lazy"
            decoding="async"
            className="w-full h-full object-contain p-4 transition-transform duration-300 group-hover/img:scale-[1.03]"
            onError={(e) => { e.currentTarget.style.display = "none"; }}
            data-testid={`product-image-${p.slug}`}
          />
        ) : (
          <div
            className="w-full h-full flex flex-col items-center justify-center text-gray-300 bg-gradient-to-br from-gray-50 to-gray-100"
            data-testid={`product-image-fallback-${p.slug}`}
          >
            <Tag className="w-10 h-10 mb-2 text-[#082c50]/30" />
            <span className="text-[10px] uppercase tracking-wider font-bold text-[#082c50]/40">5999Cargo</span>
          </div>
        )}
      </div>
      {/* Phase 19-C — Gallery thumbnail strip */}
      {(galleryItems.length > 0 || p.image_url) && galleryItems.length > 0 && (
        <div
          className="px-3 pt-2 pb-1 border-b border-slate-100 overflow-x-auto flex gap-2 scrollbar-thin"
          data-testid={`product-gallery-strip-${p.slug}`}
        >
          {/* Main image thumbnail (always first) */}
          {p.image_url && (
            <button
              type="button"
              onClick={() => { setGalleryIdx(null); openLightbox(p.image_url); }}
              className={`flex-shrink-0 w-12 h-12 border-2 ${galleryIdx === null && !variantImage ? "border-[#0055FF]" : "border-transparent"} bg-white hover:border-[#0055FF]/60 overflow-hidden transition-colors`}
              title="Hoofdfoto"
              data-testid={`product-gallery-thumb-main-${p.slug}`}
            >
              <img
                src={p.image_url}
                alt={p.image_alt || p.name}
                className="w-full h-full object-contain"
                loading="lazy"
              />
            </button>
          )}
          {galleryItems.map((g, i) => (
            <button
              key={`${g.url}-${i}`}
              type="button"
              onClick={() => { setGalleryIdx(i); openLightbox(g.url); }}
              className={`flex-shrink-0 w-12 h-12 border-2 ${galleryIdx === i && !variantImage ? "border-[#0055FF]" : "border-transparent"} bg-white hover:border-[#0055FF]/60 overflow-hidden transition-colors`}
              title={g.title || g.alt}
              data-testid={`product-gallery-thumb-${p.slug}-${i}`}
            >
              <img
                src={g.url}
                alt={g.alt}
                className="w-full h-full object-contain"
                loading="lazy"
                onError={(e) => { e.currentTarget.style.display = "none"; }}
              />
            </button>
          ))}
        </div>
      )}

      {/* Phase 19-D — fullscreen lightbox */}
      <ProductLightbox
        open={lightboxOpen}
        onClose={() => setLightboxOpen(false)}
        images={lightboxImages}
        initialIndex={lightboxInitial}
        productName={p.name}
      />
      <div className="p-5 flex flex-col flex-1">
        {p.category?.name && (
          <span className="text-[10px] uppercase tracking-wider text-[#1a5193] font-bold">
            {p.category.name}
          </span>
        )}
        <h3 className="font-chivo font-bold text-lg text-[#082c50] mt-1 leading-tight">
          {p.name}
        </h3>
        {p.short_description && (
          <p className="text-sm text-gray-600 mt-2 leading-relaxed flex-1 line-clamp-3">
            {p.short_description}
          </p>
        )}
        {p.sku && <p className="text-[11px] text-gray-400 mt-2 font-mono">SKU {p.sku}</p>}

        {isVariable && variants.length > 0 && (
          <div className="mt-3" data-testid={`variation-picker-${p.slug}`}>
            <Label className="text-[11px] uppercase font-bold text-gray-600">Variant</Label>
            <Select value={variationId} onValueChange={setVariationId}>
              <SelectTrigger className="rounded-xl border-gray-300 mt-1" data-testid={`variation-select-${p.slug}`}>
                <SelectValue placeholder="Kies variant" />
              </SelectTrigger>
              <SelectContent>
                {variants.map((v) => (
                  <SelectItem key={v.id} value={v.id}>
                    {v.name} — {fmtEur(effectivePrice(v))}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        <div className="mt-4 flex items-center justify-between">
          {price !== null ? (
            <span className="font-chivo font-black text-2xl text-[#082c50]" data-testid={`product-price-${p.slug}`}>
              {fmtEur(price)}
            </span>
          ) : (
            <span className="text-sm text-gray-500">Prijs op aanvraag</span>
          )}
          <span className={`text-xs font-bold uppercase tracking-wider ${STOCK_COLORS[stockKey]}`} data-testid={`product-stock-${p.slug}`}>
            {STOCK_LABELS[stockKey] || stockKey}
          </span>
        </div>

        {/* Quantity */}
        {buyable && !outOfStock && price !== null && (
          <div className="flex items-center gap-2 mt-3">
            <button
              type="button"
              onClick={() => setQty((q) => Math.max(1, q - 1))}
              className="w-8 h-8 border border-gray-300 flex items-center justify-center hover:bg-gray-50"
              data-testid={`product-qty-decrease-${p.slug}`}
            >
              <Minus className="w-3 h-3" />
            </button>
            <span className="text-sm w-8 text-center font-bold" data-testid={`product-qty-${p.slug}`}>{qty}</span>
            <button
              type="button"
              onClick={() => setQty((q) => q + 1)}
              className="w-8 h-8 border border-gray-300 flex items-center justify-center hover:bg-gray-50"
              data-testid={`product-qty-increase-${p.slug}`}
            >
              <Plus className="w-3 h-3" />
            </button>
          </div>
        )}

        <div className="mt-4">
          {!buyable ? (
            isService ? (
              <Button
                asChild
                className="w-full bg-[#082c50] hover:bg-[#082c50] text-white rounded-xl font-chivo font-bold uppercase text-xs"
                data-testid={`request-quote-${p.slug}`}
              >
                <Link to="/contact?get-quote=1">Vraag offerte aan</Link>
              </Button>
            ) : (
              <div
                className="text-[11px] text-amber-800 bg-amber-50 border border-amber-200 px-3 py-2 flex items-start gap-2"
                data-testid={`product-not-available-${p.slug}`}
              >
                <AlertCircle className="w-3 h-3 mt-0.5 flex-shrink-0" />
                <span>Dit product is nog niet beschikbaar voor online betaling.</span>
              </div>
            )
          ) : (
            <Button
              onClick={() => onAdd(p, sellable, qty)}
              disabled={outOfStock || price === null}
              data-testid={`add-to-cart-${p.slug}`}
              className="w-full bg-[#082c50] hover:bg-[#082c50] text-white rounded-xl font-chivo font-bold uppercase text-xs"
            >
              <ShoppingCart className="w-4 h-4 mr-2" />
              Toevoegen aan winkelwagen
            </Button>
          )}
        </div>
      </div>
    </motion.div>
  );
}

// ==================================================================
// CART PANEL
// ==================================================================
function CartPanel({ onCheckoutClick }) {
  const { items, updateQty, removeItem, totals } = useCart();
  return (
    <div className="lg:sticky lg:top-24 card-premium p-7" data-testid="cart-panel">
      <div className="flex items-center gap-2 mb-4">
        <ShoppingCart className="w-5 h-5 text-[#082c50]" />
        <h3 className="font-chivo font-black text-xl text-[#082c50]">
          Winkelwagen ({totals.count})
        </h3>
      </div>

      {items.length === 0 ? (
        <p className="text-sm text-gray-500 py-8 text-center" data-testid="cart-empty">
          Uw winkelwagen is leeg.
        </p>
      ) : (
        <>
          <ul className="divide-y divide-gray-200">
            {items.map((it) => {
              const key = `${it.product_id}::${it.variation_id || ""}`;
              return (
                <li key={key} className="py-3 flex gap-3" data-testid={`cart-item-${it.slug}-${it.variation_id || "novar"}`}>
                  {it.image && (
                    <img
                      src={it.image}
                      alt={it.image_alt || it.name}
                      loading="lazy"
                      className="w-16 h-16 object-contain bg-gray-50 p-1"
                      onError={(e) => { e.currentTarget.style.display = "none"; }}
                    />
                  )}
                  <div className="flex-1">
                    <div className="text-sm font-bold text-[#082c50] line-clamp-2">{it.name}</div>
                    {it.variation_name && (
                      <div className="text-xs text-gray-500">Variant: {it.variation_name}</div>
                    )}
                    {it.sku && <div className="text-[10px] text-gray-400 font-mono">SKU {it.sku}</div>}
                    <div className="text-xs text-gray-500">{fmtEur(it.price)}</div>
                    <div className="mt-2 flex items-center gap-2">
                      <button
                        onClick={() => updateQty(it.product_id, it.variation_id, it.quantity - 1)}
                        className="w-7 h-7 border border-gray-300 flex items-center justify-center hover:bg-gray-100"
                        data-testid={`cart-qty-decrease-${it.slug}`}
                      >
                        <Minus className="w-3 h-3" />
                      </button>
                      <span className="text-sm w-6 text-center" data-testid={`cart-qty-${it.slug}`}>{it.quantity}</span>
                      <button
                        onClick={() => updateQty(it.product_id, it.variation_id, it.quantity + 1)}
                        className="w-7 h-7 border border-gray-300 flex items-center justify-center hover:bg-gray-100"
                        data-testid={`cart-qty-increase-${it.slug}`}
                      >
                        <Plus className="w-3 h-3" />
                      </button>
                      <button
                        onClick={() => removeItem(it.product_id, it.variation_id)}
                        className="ml-auto text-gray-400 hover:text-red-500"
                        data-testid={`cart-remove-${it.slug}`}
                        aria-label="Verwijderen"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>

          <div className="mt-4 pt-4 border-t border-gray-300 space-y-1 text-sm" data-testid="cart-totals">
            <div className="flex justify-between text-gray-600">
              <span>Subtotaal incl. BTW</span>
              <span className="font-bold" data-testid="cart-subtotal">{fmtEur(totals.subtotal)}</span>
            </div>
            <div className="flex justify-between text-gray-500 text-xs">
              <span>Waarvan BTW</span>
              <span data-testid="cart-vat">{fmtEur(totals.vat)}</span>
            </div>
            <div className="flex justify-between text-base pt-2 border-t border-gray-200 mt-2">
              <span className="font-chivo font-black text-[#082c50]">Totaal</span>
              <span className="font-chivo font-black text-[#082c50]" data-testid="cart-total">{fmtEur(totals.subtotal)}</span>
            </div>
          </div>

          <Button
            onClick={onCheckoutClick}
            data-testid="cart-checkout-button"
            disabled={!items.length}
            className="w-full bg-[#1a5193] hover:bg-[#194d8d] text-white rounded-xl font-chivo font-bold uppercase text-sm py-6 mt-4"
          >
            Afrekenen
          </Button>
          <div className="flex items-center justify-center gap-1 mt-3 text-xs text-gray-500">
            <Lock className="w-3 h-3" />
            Veilig betalen via Stripe
          </div>
        </>
      )}
    </div>
  );
}

// ==================================================================
// PAGE
// ==================================================================
function ShopAccountBanner() {
  const { customer, logout } = useShopAuth();
  if (customer) {
    return (
      <div className="bg-blue-50 border-b border-blue-200" data-testid="webshop-shopaccount-banner-logged-in">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-2.5 flex flex-wrap items-center justify-between gap-2 text-xs">
          <span className="text-blue-900">
            Ingelogd als <strong>{customer.first_name || customer.email}</strong> — uw gegevens worden automatisch ingevuld bij afrekenen.
          </span>
          <div className="flex items-center gap-2">
            <Link to="/shop-account" className="text-blue-900 font-bold underline" data-testid="webshop-link-my-orders">Mijn bestellingen</Link>
            <Link to="/shop-account/profile" className="text-blue-900 font-bold underline" data-testid="webshop-link-my-profile">Profiel</Link>
            <button onClick={logout} className="text-red-600 font-bold underline" data-testid="webshop-link-logout">Uitloggen</button>
          </div>
        </div>
      </div>
    );
  }
  return (
    <div className="bg-gray-50 border-b border-gray-200" data-testid="webshop-shopaccount-banner-guest">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-2.5 flex flex-wrap items-center justify-between gap-2 text-xs">
        <span className="text-gray-700">
          Heeft u al een shopaccount? Log in om sneller te bestellen — bezorgadres en gegevens worden automatisch ingevuld.
        </span>
        <div className="flex items-center gap-3">
          <Link to="/shop-account/login" className="text-[#082c50] font-bold underline" data-testid="webshop-link-login">Inloggen</Link>
          <Link to="/shop-account/register" className="text-[#1a5193] font-bold underline" data-testid="webshop-link-register">Account aanmaken</Link>
        </div>
      </div>
    </div>
  );
}

export default function WebshopPage() {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState({ q: "", category_slug: "", product_type: "" });
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const { addItem } = useCart();

  const load = async () => {
    setLoading(true);
    try {
      const params = Object.fromEntries(Object.entries(filter).filter(([_, v]) => v));
      const [p, c] = await Promise.all([
        fetchWebshopCatalog(params),
        fetchWebshopCategories(),
      ]);
      setProducts(p);
      setCategories(c);
    } catch (err) {
      toast.error("Kon webshop niet laden");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [filter.category_slug, filter.product_type]);

  // search runs on debounce
  useEffect(() => {
    const id = setTimeout(load, 250);
    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter.q]);

  const visibleProducts = useMemo(() => products, [products]);

  const handleAdd = (product, sellable, qty) => {
    const variant = sellable && sellable.id !== product.id ? sellable : null;
    const price = effectivePrice(sellable);
    addItem({
      product_id: product.id,
      variation_id: variant?.id || null,
      name: product.name,
      variation_name: variant?.name || null,
      sku: sellable?.sku || product.sku,
      slug: product.slug,
      image: (variant && variant.image_url) || product.image_url,
      image_alt: product.image_alt,
      price,
      regular_price: sellable?.regular_price ?? product.regular_price,
      sale_price: sellable?.sale_price ?? product.sale_price,
      vat_percentage: sellable?.vat_percentage ?? product.vat_percentage,
      stripe_price_id: variant?.stripe_price_id || product.stripe_price_id,
      stock_status: sellable?.stock_status,
      stock_qty: sellable?.stock_qty,
      is_service: product.product_type === "service",
    }, qty || 1);
    toast.success(`${product.name}${variant ? " — " + variant.name : ""} toegevoegd`);
  };

  return (
    <>
      <SEO
        title="Shop — Verhuisdozen, verpakkingsmateriaal & cargo-services | 5999Cargo"
        description="Bestel verhuisdozen, verpakkingsmateriaal en cargo-services in onze online shop. Veilig betalen via Stripe en ophalen of laten leveren in Nederland — voor verzending naar de Caribbean."
        keywords="verhuisdozen kopen, verpakkingsmateriaal cargo, 5999Cargo shop, verhuisdoos Rotterdam, materiaal verzending Caribbean"
      />

      {/* SEO Sprint 2 — per-product Product schema (safe defaults).
          Filtered to products with name + at least 1 price or variant
          so we never emit empty offers. */}
      {visibleProducts.map((p) => (
        <Schema
          key={`schema-product-${p.id}`}
          type="Product"
          data={{ product: p }}
        />
      ))}

      <WebshopPageBanner />

      {/* SEO Sprint 2 — breadcrumbs (Home → Webshop) */}
      <div className="bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
          <Breadcrumbs
            items={[
              { name: "Home", url: "/" },
              { name: "Webshop" },
            ]}
            testid="webshop-breadcrumbs"
          />
        </div>
      </div>

      {/* Phase B-Polish — shopaccount status banner */}
      <ShopAccountBanner />

      <section className="py-12 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-8">
            {/* Filters */}
            <div className="flex flex-wrap gap-3 mb-6 items-center" data-testid="webshop-filters">
              <div className="relative flex-1 min-w-[200px]">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <Input
                  placeholder="Zoeken op naam of SKU…"
                  value={filter.q}
                  onChange={(e) => setFilter({ ...filter, q: e.target.value })}
                  className="rounded-xl pl-9 border-gray-300"
                  data-testid="filter-search"
                />
                {filter.q && (
                  <button
                    onClick={() => setFilter({ ...filter, q: "" })}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-700"
                    data-testid="filter-search-clear"
                    aria-label="Zoekfilter wissen"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
              <Select
                value={filter.category_slug || "__all"}
                onValueChange={(v) => setFilter({ ...filter, category_slug: v === "__all" ? "" : v })}
              >
                <SelectTrigger className="rounded-xl border-gray-300 w-52" data-testid="filter-category">
                  <SelectValue placeholder="Alle categorieën" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all">Alle categorieën</SelectItem>
                  {categories.map((c) => (
                    <SelectItem key={c.slug} value={c.slug}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select
                value={filter.product_type || "__all"}
                onValueChange={(v) => setFilter({ ...filter, product_type: v === "__all" ? "" : v })}
              >
                <SelectTrigger className="rounded-xl border-gray-300 w-44" data-testid="filter-type">
                  <SelectValue placeholder="Alle types" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all">Alle types</SelectItem>
                  <SelectItem value="simple">Producten</SelectItem>
                  <SelectItem value="variable">Met varianten</SelectItem>
                  <SelectItem value="service">Services</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Grid */}
            {loading ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6" data-testid="product-grid-loading">
                {[...Array(4)].map((_, i) => (
                  <div key={i} className="h-96 bg-gray-100 animate-pulse" />
                ))}
              </div>
            ) : visibleProducts.length === 0 ? (
              <div className="bg-gray-50 border border-gray-200 p-10 text-center" data-testid="product-grid-empty">
                <p className="text-gray-600">Geen producten gevonden voor deze filters.</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6" data-testid="product-grid">
                {visibleProducts.map((p) => (
                  <ProductCard key={p.id} p={p} onAdd={handleAdd} />
                ))}
              </div>
            )}
          </div>

          <aside className="lg:col-span-4">
            <CartPanel onCheckoutClick={() => setCheckoutOpen(true)} />
          </aside>
        </div>
      </section>

      <CheckoutWizard open={checkoutOpen} onOpenChange={setCheckoutOpen} />

      <CtaBand
        title="Heeft u een vraag of wenst u een offerte?"
        subtitle="Neem contact op voor maatwerk shipping naar Curaçao en het Caribisch gebied."
        primaryHref="/contact?get-quote=1"
        primaryLabel="Vraag offerte aan"
      />
    </>
  );
}
